import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Colors } from "../../config/Constants";
import TimeField from "./TimeField";
import { tConvert } from "../../helper";

interface Props {
  placeholder: string;
  label?: string;
  onTimeChange: (event: any, value: string) => void;
  value?: any;
  name?: string;
  required: boolean;
  helperText: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

interface StyledInputProps {
  helperText: string;
  fullWidth: boolean;
}

const TimeRangeSelector = ({
  placeholder,
  label,
  value,
  name,
  required,
  helperText,
  disabled,
  onTimeChange,
  fullWidth,
}: Props) => {
  const [dropdown, setShowDropdown] = useState(false);

  const getLabel = (value: any) => {
    if(value.start === value.end){
      return ""
    }
    if (value.start || value.end) {
      return `${tConvert(value.start)} - ${tConvert(value.end)}`;
    } else {
      return "";
    }
  };

  const useOutsideAlerter = (ref: any) => {
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowDropdown(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  };
  const wrapperRef = useRef(null);

  useOutsideAlerter(wrapperRef);

  return (
    <>
      <InputFieldWrapper ref={wrapperRef}>
        {label && (
          <Label>
            {label}
            {required && <span>*</span>}
          </Label>
        )}
        <InputWrapper>
          <InputField
            disabled={disabled}
            placeholder={placeholder}
            value={getLabel(value)}
            name={name}
            helperText={helperText}
            autoComplete="off"
            onClick={() => setShowDropdown(true)}
            // @ts-ignore
            fullWidth={fullWidth}
          />
          <TimeIcon
            src={require("../../assets/images/time.png")}
            alt="clear"
            onClick={() => setShowDropdown(true)}
          />
        </InputWrapper>
        {helperText && <Error>{helperText}</Error>}
        {dropdown && (
          <DropdownList>
            <FieldWrapper>
              <TimeField
                onChange={onTimeChange}
                value={value.start}
                label="Start Time"
                name="start"
              />
            </FieldWrapper>
            <FieldWrapper>
              <TimeField
                onChange={onTimeChange}
                value={value.end}
                label="End Time"
                name="end"
              />
            </FieldWrapper>
          </DropdownList>
        )}
      </InputFieldWrapper>
    </>
  );
};

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const InputFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 1rem;
`;

const DropdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: absolute;
  top: 4.5rem;
  width: 100%;
  background: white;
  border: 1px solid ${Colors.athensGrey};
  border-radius: 0.25rem;
  max-height: 15rem;
  overflow-y: auto;
  padding: 1rem;
  box-sizing: border-box;
  z-index: 99;
  max-width: 12rem;
  @media only screen and (max-width: 768px) {
    max-width: 100%;
  }
`;

const InputField = styled.input<StyledInputProps>`
  width: ${(props) => (props.fullWidth ? "100%" : "12rem")};
  align-items: center;
  font-family: "Poppins-Medium";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 12px;
  padding: 0.75rem 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid
    ${(props) => (props.helperText ? Colors.red : Colors.athensGrey)};
  ::placeholder {
    color: ${Colors.autumnFog};
  }
  :focus {
    outline: none;
  }
  :disabled {
    background: ${Colors.white};
    color: ${Colors.cloudyGrey};
    cursor: no-drop;
  }
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;
const Label = styled.label`
  font-family: "Poppins-Medium";
  font-style: normal;
  font-weight: 350;
  font-size: 13px;
  line-height: 16px;
  color: ${Colors.electricBlue};
  margin-bottom: 0.25rem;
  > span {
    color: ${Colors.red};
    margin-left: 2px;
  }
`;

const Error = styled.span`
  color: ${Colors.red};
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const TimeIcon = styled.img`
  cursor: pointer;
  width: 1.25rem;
  height: 1.25rem;
  position: absolute;
  background: white;
  position: absolute;
  right: 0.5rem;
  top: 0.75rem;
  @media only screen and (max-width: 768px){
  right: -0.75rem

  }
`;

export default TimeRangeSelector;
